import {Component, ElementRef, ViewChild, OnDestroy, OnInit, EventEmitter, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {parse} from 'mrz';
import {OcrSpaceService, OcrSpaceResponse} from '../../core/services/ocr-space.service';
import {catchError, finalize, timeout, throwError} from 'rxjs';
import {SquareButtonComponent} from '../../shared/components/square-button/square-button.component';

@Component({
  selector: 'app-mrz-camera',
  standalone: true,
  imports: [CommonModule, FormsModule, SquareButtonComponent],
  template: `
    <div style="padding:16px; max-width: 1000px;">
      <div #videoContainer style="
                position:relative;
                width:100%;
                aspect-ratio: 4;
                border-radius:12px;
                overflow:hidden;
                background:#111;
              ">
        <video #video autoplay playsinline muted
               style="
                    position:absolute;
                    inset:0;
                    width:100%;
                    height:100%;
                    object-fit: cover;
                    display:block;
                    z-index:1;
                    border-radius:12px;
                  "></video>

        <div style="
                  position:absolute;
                  inset:0;
                  z-index:5;
                  pointer-events:none;
                  border:2px dashed rgba(255,255,255,0.95);
                  border-radius:12px;
                  box-shadow: inset 0 0 20px rgba(255,255,255,0.10);
                  background: rgba(255,255,255,0.04);
                "></div>
      </div>

      <div class="button-container" style="text-align:center; margin-top:12px;">
        <square-button [label]="'Leer Banda MRZ'" (click)="capture()"></square-button>
      </div>

      <canvas #canvas style="display:none;"></canvas>
    </div>
  `
})
export class MrzCameraComponent implements OnInit, OnDestroy {

  @Output() mrzSuccess = new EventEmitter<any>();
  @Output() mrzPartialSuccess = new EventEmitter<any>();
  @Output() mrzError = new EventEmitter<string>();
  @Output() mrzCapture = new EventEmitter<string>();
  @Output() mrzStage = new EventEmitter<{ stage: string; message?: string; imageUrl?: string; data?: any }>();


  @ViewChild('video', {static: true}) videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas', {static: true}) canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('videoContainer', {static: false}) videoContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('cropFrame', {static: false}) cropFrame!: ElementRef<HTMLDivElement>;

  cameraOn = false;
  loading = false;
  errorMsg = '';

  previewUrl: string | null = null;
  test = false;

  // Configuración del recuadro de captura (en porcentajes)
  readonly cropConfig = {
    left: 6,      // %
    right: 6,     // %
    bottom: 10,   // %
    height: 26    // %
  };

  mrzLines: string[] = [];
  mrzLinesDebug = '';
  fields: any = null;
  fieldEntries: { key: string, value: string }[] = [];

  strictValid = false;
  relaxedValid = false;

  private stream: MediaStream | null = null;
  private facingMode: 'environment' | 'user' = 'environment';

  constructor(private ocr: OcrSpaceService) {
  }

  ngOnInit(): void {
    this.startCamera();
  }

  ngOnDestroy() {
    this.stopCamera();
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
  }

  async startCamera() {
    try {
      this.mrzStage.emit({ stage: 'camera-start', message: 'Iniciando cámara...' });

      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: {ideal: this.facingMode},
          width: {ideal: 4096, min: 1920},
          height: {ideal: 2160, min: 1080}
        },
        audio: false
      });

      this.videoRef.nativeElement.srcObject = this.stream;
      await this.videoRef.nativeElement.play();
      this.cameraOn = true;

      this.mrzStage.emit({ stage: 'camera-ready', message: 'Cámara lista' });
    } catch (e: any) {
      this.errorMsg = 'Error cámara: ' + e.message;
      this.mrzStage.emit({ stage: 'camera-error', message: this.errorMsg });
    }
  }

  stopCamera() {
    if (this.stream) {
      this.stream.getTracks().forEach(t => t.stop());
      this.stream = null;
    }
    this.cameraOn = false;
  }

  async switchCamera() {
    this.facingMode = this.facingMode === 'environment' ? 'user' : 'environment';
    if (this.cameraOn) {
      await this.startCamera();
    }
  }

  /**
   * Calcula exactamente qué píxeles del video stream corresponden al recuadro visual
   */
  private calculateCropCoordinates() {
    const video = this.videoRef.nativeElement;
    const container = this.videoContainer.nativeElement;

    const videoW = video.videoWidth;
    const videoH = video.videoHeight;

    const containerW = container.offsetWidth;
    const containerH = container.offsetHeight;

    const videoAspect = videoW / videoH;
    const containerAspect = containerW / containerH;

    let displayW: number;
    let displayH: number;
    let offsetX = 0;
    let offsetY = 0;

    if (videoAspect > containerAspect) {
      displayH = containerH;
      displayW = containerH * videoAspect;
      offsetX = (containerW - displayW) / 2;
    } else {
      displayW = containerW;
      displayH = displayW / videoAspect;
      offsetY = (containerH - displayH) / 2;
    }

    const scaleX = videoW / displayW;
    const scaleY = videoH / displayH;

    return {
      x: Math.max(0, Math.round((-offsetX) * scaleX)),
      y: Math.max(0, Math.round((-offsetY) * scaleY)),
      width: Math.round(containerW * scaleX),
      height: Math.round(containerH * scaleY)
    };
  }

  async capture() {
    this.mrzStage.emit({ stage: 'capture-start', message: 'Capturando imagen...' });
    console.log('capture');
    this.mrzCapture.emit();
    this.errorMsg = '';
    this.loading = true;
    this.resetResults();

    try {
      const video = this.videoRef.nativeElement;
      const canvas = this.canvasRef.nativeElement;

      const crop = this.calculateCropCoordinates();
      this.mrzStage.emit({ stage: 'crop-calculated', message: 'Calculando recorte...', data: crop });

      console.log('Crop coordinates:', crop);

      const targetW = 4800;
      const scale = targetW / crop.width;
      const targetH = Math.round(crop.height * scale);

      canvas.width = targetW;
      canvas.height = targetH;

      const ctx = canvas.getContext('2d')!;
      ctx.imageSmoothingEnabled = false;

      // Dibujar EXACTAMENTE lo que está dentro del recuadro visual
      ctx.drawImage(
        video,
        crop.x, crop.y, crop.width, crop.height,  // Fuente: área del recuadro en el stream
        0, 0, targetW, targetH                      // Destino: canvas completo
      );

      const imgData = ctx.getImageData(0, 0, targetW, targetH);
      ctx.putImageData(imgData, 0, 0);

      const blob = await new Promise<Blob>((resolve) =>
        canvas.toBlob(b => resolve(b!), 'image/jpeg', 0.9)
      );

      this.previewUrl = URL.createObjectURL(blob);

      this.mrzStage.emit({
        stage: 'image-captured',
        message: 'Imagen capturada',
        imageUrl: this.previewUrl
      });

      const file = new File([blob], 'mrz.jpg', {type: 'image/jpeg'});

      this.mrzStage.emit({ stage: 'ocr-start', message: 'Enviando imagen al OCR...' });

      this.ocr.ocrMrz(file).pipe(
        timeout(20000),
        catchError(err => throwError(() => err)),
        finalize(() => this.loading = false)
      ).subscribe({
        next: (res: OcrSpaceResponse) => {
          this.mrzStage.emit({ stage: 'ocr-response', message: 'Respuesta OCR recibida', data: res });

          console.log('OCR', res);
          const text = res.ParsedResults?.[0]?.ParsedText ?? '';
          this.mrzStage.emit({ stage: 'ocr-text', message: 'Texto OCR recibido', data: text });

          this.mrzLines = this.extractMrz(text);
          console.log('MRZ lines', this.mrzLines);
          this.mrzLinesDebug = this.mrzLines.join('\n');

          this.mrzStage.emit({
            stage: 'mrz-lines',
            message: 'Líneas MRZ extraídas',
            data: this.mrzLines
          });

          try {
            const parsed: any = parse(this.mrzLines.join('\n'));
            this.fields = parsed.fields;
            console.log('fields', this.fields);
            this.fieldEntries = Object.entries(this.fields || {})
              .map(([k, v]) => ({key: k, value: String(v)}));

            this.strictValid = !!parsed.valid;
            this.relaxedValid = !!parsed.valid;

            this.mrzStage.emit({
              stage: 'parsed',
              message: this.relaxedValid ? 'MRZ parseada correctamente' : 'MRZ parseada con aviso',
              data: this.fields
            });

            if (this.relaxedValid) {
              this.mrzSuccess.emit(this.fields);
            } else if (null !== this.fields) {
              this.mrzPartialSuccess.emit(this.fields);
            } else {
              this.errorMsg = 'No válido';
              this.mrzError.emit(this.errorMsg);
              this.mrzStage.emit({ stage: 'error', message: this.errorMsg });
            }
          } catch (e: any) {
            if (null !== this.fields) {
              this.mrzPartialSuccess.emit(this.fields);
              this.mrzStage.emit({
                stage: 'partial-success',
                message: 'Parseo parcial',
                data: this.fields
              });
            } else {
              console.error('Exception', e);
              this.errorMsg = 'Error parseando MRZ';
              this.mrzError.emit(this.errorMsg);
              this.mrzStage.emit({ stage: 'error', message: this.errorMsg });
            }
          }
        },
        error: (err) => {
          this.errorMsg = 'OCR falló o timeout';
          this.mrzError.emit(this.errorMsg);
          this.mrzStage.emit({ stage: 'ocr-error', message: this.errorMsg, data: err });
        }
      });

    } catch (e: any) {
      this.loading = false;
      this.errorMsg = e.message;
      this.mrzError.emit(this.errorMsg);
      this.mrzStage.emit({ stage: 'capture-error', message: this.errorMsg });
    }
  }

  private binarize(data: Uint8ClampedArray) {
    let sum = 0;
    for (let i = 0; i < data.length; i += 4) {
      const gray = data[i] * 0.3 + data[i + 1] * 0.59 + data[i + 2] * 0.11;
      sum += gray;
    }
    const avg = sum / (data.length / 4);

    for (let i = 0; i < data.length; i += 4) {
      const gray = data[i] * 0.3 + data[i + 1] * 0.59 + data[i + 2] * 0.11;
      const v = gray < avg ? 0 : 255;
      data[i] = data[i + 1] = data[i + 2] = v;
    }
  }

  private extractMrz(text: string): string[] {
    return text.toUpperCase()
      .split('\n')
      .map(l => l.replace(/[^A-Z0-9<]/g, '').trim())
      .filter(l => l.length >= 30)
      .slice(0, 3);
  }

  private resetResults() {
    this.mrzLines = [];
    this.mrzLinesDebug = '';
    this.fields = null;
    this.fieldEntries = [];
    this.strictValid = false;
    this.relaxedValid = false;
  }
}
